/**
 * OCT28 WP-2b-2b-i — the recorded work sessions (ticket_timer_sessions) of a technician column's started tickets: the
 * evidence the multi-bay header's remaining hands-on work is measured from (liveTicketCoverage.remainingHandsOnMinutes).
 *
 * ONE query per column, and only while it is enabled (the multi-bay layout). It is re-read when a ticket of the column
 * starts, pauses or stops — the ticket row's timer_status / current_session_id / paused_at change, which every device
 * receives through the board's tickets subscription — and on the board's `ticket-updated` event. A running session
 * needs no re-read: its minutes are measured against the minute tick. ticket_timer_sessions is not in the realtime
 * publication, so no channel is opened for it.
 *
 * Unknown is not "no work": a ticket whose sessions have not been read successfully (first load, a failed read, a
 * ticket that joined the column since the last read) is reported as 'unavailable', never as an empty list.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { WorkEvidence, WorkSessionLike } from '@/lib/scheduling/liveTicketCoverage';

interface Loaded { ids: ReadonlySet<string>; byTicket: ReadonlyMap<string, WorkSessionLike[]> }

const NONE: Loaded = { ids: new Set(), byTicket: new Map() };

/** The evidence for one ticket from what was read: its sessions if it was in the read, else 'unavailable'. */
export function workEvidenceFor(loaded: Loaded, ticketId: string): WorkEvidence {
  return loaded.ids.has(ticketId) ? (loaded.byTicket.get(ticketId) ?? []) : 'unavailable';
}

export function useTicketWorkSessions(ticketIds: readonly string[], refreshKey: string, enabled: boolean): (ticketId: string) => WorkEvidence {
  const [loaded, setLoaded] = useState<Loaded>(NONE);
  const idsKey = [...ticketIds].sort().join(',');

  useEffect(() => {
    if (!enabled || idsKey === '') { setLoaded(NONE); return; }
    const ids = idsKey.split(',');
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase
        .from('ticket_timer_sessions')
        .select('ticket_id, technician_id, started_at, stopped_at, duration_min')
        .in('ticket_id', ids);
      if (cancelled) return;
      if (error) {
        // the last successful read no longer describes the column: report every ticket as unknown
        console.warn('[useTicketWorkSessions] read failed:', error.message);
        setLoaded(NONE);
        return;
      }
      const byTicket = new Map<string, WorkSessionLike[]>();
      for (const s of (data ?? []) as Array<WorkSessionLike & { ticket_id: string }>) {
        const list = byTicket.get(s.ticket_id);
        if (list) list.push(s); else byTicket.set(s.ticket_id, [s]);
      }
      setLoaded({ ids: new Set(ids), byTicket });
    };
    void load();
    const onTicketUpdated = () => { void load(); };
    window.addEventListener('ticket-updated', onTicketUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('ticket-updated', onTicketUpdated);
    };
  }, [enabled, idsKey, refreshKey]);

  return (ticketId: string) => workEvidenceFor(loaded, ticketId);
}
